/* eslint-disable no-param-reassign */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-undef */
import 'amazon-connect-streams';
import {
  API_PATIENTS_COMMENTS,
  API_READINGS_ACTIONS,
  API_CCM_PATIENT_CALL,
} from 'services/dataService/resources';
import { showResult } from 'infrastructure/hooks/utils';
import { SocketRoutes } from 'services/wsService';
import { differenceInSeconds } from 'date-fns';
import {
  emitCallEndedEvent,
  emitCallStartedEvent,
} from 'infrastructure/customEvents/callEvents';
import { ModuleType } from 'infrastructure/enums';

import dataService from '../dataService';

import type { TActionFrom } from 'infrastructure/interfaces/common';
import type WS from 'services/wsService';
import type { Orchestration } from 'aws-rum-web/dist/cjs/orchestration/Orchestration';

const AWS_CONNECT_URL_CCP = process.env.REACT_APP_CONNECT_URL_CCP!;
const AWS_CONNECT_URL = process.env.REACT_APP_CONNECT_URL!;

interface ConnectDetails {
  patientGuid: string;
  connectedAt?: Date;
}

const contactToPatientMap: Map<string, ConnectDetails> = new Map();

interface ConnectCallbacks {
  onCallStarted?: (
    callId: string,
    patientGuid: string,
    isRpmCall: boolean,
  ) => void;
  onCallEnded?: () => void;
}

interface IInitProps {
  phoneNumber: string;
  patientGuid: string;
  setCallId: (callId: string) => void;
  isRpmCall: boolean;
  deviceId: string;
  readingId: string;
  wsClient: typeof WS;
  awsRumAgent: Orchestration | null;
  isReadOnlyRole: boolean;
  actionFrom?: TActionFrom;
  callbacks?: ConnectCallbacks;
}

// Mutable state updated on every init — read by the single registered contact handler
let currentProps: IInitProps | null = null;
let contactHandlerRegistered = false;

const init = (props: IInitProps) => {
  const { phoneNumber } = props;

  // Always update props so the single handler uses fresh values each session
  currentProps = props;

  const containerDiv = document.getElementById('connect-el')!;
  connect.core.initCCP(containerDiv, {
    ccpUrl: AWS_CONNECT_URL_CCP,
    loginUrl: AWS_CONNECT_URL,
    loginPopup: true,
    loginPopupAutoClose: true,
    region: 'us-east-1',
    pageOptions: {
      enableAudioDeviceSettings: false,
      enablePhoneTypeSettings: false,
    },
    softphone: {
      allowFramedSoftphone: true,
    },
  });

  if (phoneNumber) {
    connect.agent((agent) => {
      const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
      agent.connect(endpoint);
    });

    // connect.contact() subscriptions persist in the SDK and cannot be removed.
    // Register the handler exactly once for the lifetime of the page.
    if (!contactHandlerRegistered) {
      contactHandlerRegistered = true;

      connect.contact((contact) => {
        contact.onConnecting(async (event) => {
          console.log('onConnectingContact', contact);
          console.log('onConnectingEvent', event);
          console.log('onConnectingContactIsInbound', contact.isInbound());
          console.log('onConnectingEventIsInbound', event.isInbound());
          if (event.isInbound()) return;

          const callProps = currentProps;
          if (!callProps || !callProps.patientGuid) return;

          const { contactId } = event;

          callProps.setCallId(contactId);
          callProps.awsRumAgent?.recordEvent('callConnecting', {
            patientGuid: callProps.patientGuid,
            phoneNumber: callProps.phoneNumber,
            contactId,
          });

          let callData = {};
          callData = {
            patientGuid: callProps.patientGuid,
            actionFrom: callProps.actionFrom,
          };

          contactToPatientMap.set(contactId, {
            patientGuid: callProps.patientGuid,
          });
          callProps.wsClient.send(SocketRoutes.OnCallStarted, {
            patientGuid: callProps.patientGuid,
            isRpmCall: callProps.isRpmCall,
          });
          callProps.callbacks?.onCallStarted?.(
            contactId,
            callProps.patientGuid,
            callProps.isRpmCall,
          );

          if (callProps.isRpmCall) {
            callData = {
              ...callData,
              deviceId: callProps.deviceId,
              readingId: callProps.readingId,
              type: 'call',
              message: contactId,
              commentType: 'rpm',
            };
          } else {
            callData = {
              ...callData,
              callId: contactId,
            };
          }

          if (!callProps.isReadOnlyRole) {
            const { error } = await dataService.createOne(
              !callProps.isRpmCall
                ? API_CCM_PATIENT_CALL
                : callProps.deviceId && callProps.readingId
                ? API_READINGS_ACTIONS
                : API_PATIENTS_COMMENTS,
              callData,
            );

            if (error) showResult(error);
          }

          // Clear patientGuid so a stale re-fire of onConnecting is a no-op
          currentProps = { ...callProps, patientGuid: '' };
        });

        contact.onConnected(async (event) => {
          if (event.isInbound()) return;
          const { contactId } = event;
          const currentPatient = contactToPatientMap.get(contactId);
          if (currentPatient) {
            currentPatient.connectedAt = new Date();
          }
          emitCallStartedEvent();
        });

        contact.onDestroy(async (event) => {
          if (event.isInbound()) return;
          const { contactId } = event;
          const currentPatientGuid =
            contactToPatientMap.get(contactId)?.patientGuid;
          if (currentPatientGuid) {
            const destroyProps = currentProps;
            destroyProps?.awsRumAgent?.recordEvent('callDestroyed', {
              patientGuid: currentPatientGuid,
              contactId,
            });
            destroyProps?.wsClient.send(SocketRoutes.OnCallEnded, {
              patientGuid: currentPatientGuid,
              isRpmCall: destroyProps?.isRpmCall,
            });
            destroyProps?.callbacks?.onCallEnded?.();
          }
        });

        contact.onEnded(async (event) => {
          if (event.isInbound()) return;

          const { contactId } = event;
          const currentPatient = contactToPatientMap.get(contactId);
          if (currentPatient) {
            const currentPatientGuid = currentPatient.patientGuid;
            const endedProps = currentProps;

            endedProps?.awsRumAgent?.recordEvent('callDestroyed', {
              patientGuid: currentPatientGuid,
              contactId,
            });
            endedProps?.wsClient.send(SocketRoutes.OnCallEnded, {
              patientGuid: currentPatientGuid,
              isRpmCall: endedProps?.isRpmCall,
            });
            endedProps?.callbacks?.onCallEnded?.();

            const callDurationSeconds = currentPatient.connectedAt
              ? differenceInSeconds(new Date(), currentPatient.connectedAt)
              : 0;
            emitCallEndedEvent({
              patientGuid: currentPatientGuid,
              callDuration: callDurationSeconds,
              module: endedProps?.isRpmCall ? ModuleType.RPM : ModuleType.CCM,
              callId: contactId,
            });
          }
        });
      });
    }
  }
};

const terminate = () => {
  connect.core.terminate();
  contactHandlerRegistered = false;
  const containerDiv = document.getElementById('connect-el');
  if (containerDiv) {
    const iframe = containerDiv.firstElementChild;
    if (iframe) containerDiv.removeChild(iframe);
  }
};

export { init, terminate };
